/**
 * Enrolled-stage hooks (ENR-034, ENR-039).
 *
 * Wraps the Cases/Notifications enrolled-stage actions:
 *   - POST /api/v1/cases/{id}/enrollment:confirm    → EnrollmentConfirmation
 *   - POST /api/v1/cases/{id}/patient-guide:dispatch → GuideDispatch
 *
 * Both are write-only actions; each tracks its own working/result/error state
 * and surfaces a 404 as a friendly disabled message rather than crashing
 * (the sibling backend wires these on the Cases + Notifications services).
 */
import { useCallback, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** Confirmation returned when enrollment is confirmed. */
export interface EnrollmentConfirmation {
  confirmed_at: string;
  effective_date?: string | null;
  status?: string | null;
}

/** Result of dispatching the patient guide. */
export interface GuideDispatch {
  dispatched_at: string;
  channel?: string | null;
  notification_id?: string | null;
}

interface UseEnrollmentResult {
  confirmation: EnrollmentConfirmation | null;
  dispatch: GuideDispatch | null;
  isConfirming: boolean;
  isDispatching: boolean;
  confirmError: ApiError | null;
  dispatchError: ApiError | null;
  confirmEnrollment: () => Promise<EnrollmentConfirmation | null>;
  dispatchGuide: () => Promise<GuideDispatch | null>;
}

export function useEnrollment(caseId: string | undefined): UseEnrollmentResult {
  const [confirmation, setConfirmation] = useState<EnrollmentConfirmation | null>(null);
  const [dispatch, setDispatch] = useState<GuideDispatch | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [confirmError, setConfirmError] = useState<ApiError | null>(null);
  const [dispatchError, setDispatchError] = useState<ApiError | null>(null);

  const confirmEnrollment = useCallback(async (): Promise<EnrollmentConfirmation | null> => {
    if (!caseId) return null;
    setIsConfirming(true);
    setConfirmError(null);
    try {
      const res = await apiClient.post<EnrollmentConfirmation>(
        `/cases/${encodeURIComponent(caseId)}/enrollment:confirm`,
        {}
      );
      setConfirmation(res);
      return res;
    } catch (err: unknown) {
      setConfirmError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return null;
    } finally {
      setIsConfirming(false);
    }
  }, [caseId]);

  const dispatchGuide = useCallback(async (): Promise<GuideDispatch | null> => {
    if (!caseId) return null;
    setIsDispatching(true);
    setDispatchError(null);
    try {
      const res = await apiClient.post<GuideDispatch>(
        `/cases/${encodeURIComponent(caseId)}/patient-guide:dispatch`,
        {}
      );
      setDispatch(res);
      return res;
    } catch (err: unknown) {
      setDispatchError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return null;
    } finally {
      setIsDispatching(false);
    }
  }, [caseId]);

  return {
    confirmation,
    dispatch,
    isConfirming,
    isDispatching,
    confirmError,
    dispatchError,
    confirmEnrollment,
    dispatchGuide,
  };
}
